import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Camera, Check, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { useAuth } from '../contexts/AuthContext';
import { usePosts } from '../contexts/PostContext';
import { supabase } from '../lib/supabase';
import TopicSelect from '../components/posts/TopicSelect';

const factRatingOptions = [
  { value: 'high', label: 'High - Fully Verified', color: 'bg-green-500' },
  { value: 'medium', label: 'Medium - Partially Verified', color: 'bg-yellow-500' },
  { value: 'low', label: 'Low - Unverified Claims', color: 'bg-red-500' }
];

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const CreatePost: React.FC = () => {
  const { currentUser } = useAuth();
  const { createPost, isLoading } = usePosts();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [photoURL, setPhotoURL] = useState('');
  const [factRating, setFactRating] = useState<'high' | 'medium' | 'low'>('medium');
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
  const [topic, setTopic] = useState('');
  
  if (!currentUser) return null;

  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', error => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea
  ): Promise<Blob> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setError('');
    
    // Create a temporary URL for the image
    const objectUrl = URL.createObjectURL(file);
    setPhotoURL(objectUrl);
    setIsCropping(true);
  };

  const handleCropSave = async () => {
    if (!photoURL || !croppedAreaPixels || !currentUser) return;

    setIsUploading(true);
    setError('');
    
    try {
      console.log('🔍 Starting image upload process...');
      
      // Check if user is properly authenticated
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error('Authentication required. Please log in again.');
      }
      
      const croppedImage = await getCroppedImg(photoURL, croppedAreaPixels);
      console.log('✅ Image cropped successfully, size:', croppedImage.size);
      
      // Upload directly to user's folder with proper path structure
      const fileExtension = 'jpg';
      const fileName = `${currentUser.id}/${Date.now()}-${crypto.randomUUID()}.${fileExtension}`;

      // Upload file to Supabase Storage
      const { error: uploadError, data } = await supabase.storage
        .from('posts')
        .upload(fileName, croppedImage, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('❌ Supabase upload error:', uploadError);
        throw new Error(`Upload failed: ${uploadError.message}`);
      }
      
      console.log('✅ Upload successful:', data);

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('posts')
        .getPublicUrl(fileName);
        
      console.log('🔗 Public URL generated:', publicUrl);

      // Clean up the temporary object URL
      if (photoURL.startsWith('blob:')) {
        URL.revokeObjectURL(photoURL);
      }

      // Only set the photoURL after successful upload
      setPhotoURL(publicUrl);
      setIsCropping(false);
    } catch (err) {
      console.error('Error uploading file:', err);
      
      let errorMessage = 'Failed to upload image. Please try again.';
      
      if (err instanceof Error) {
        if (err.message.includes('row-level security') || err.message.includes('authorization')) {
          errorMessage = 'Permission denied. Please check your account permissions and try again.';
        } else if (err.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'Upload timed out. Please try again.';
        } else if (err.message.includes('size')) {
          errorMessage = 'File size too large. Please use a smaller image.';
        } else if (err.message.includes('Authentication required')) {
          errorMessage = 'Please log out and log back in, then try again.';
        }
      }
      
      setError(errorMessage);
      setIsCropping(false);
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim()) {
      setError('Please enter a post title');
      return;
    }
    
    if (!content.trim()) {
      setError('Please enter post content');
      return;
    }
    
    if (!photoURL.trim()) {
      setError('Please upload a cover image');
      return;
    }
    
    // Prevent submission if photoURL is still a blob URL
    if (photoURL.startsWith('blob:')) {
      setError('Please complete the image upload process before publishing');
      return;
    }
    
    if (!topic.trim()) {
      setError('Please select a topic');
      return;
    }
    
    try {
      setError('');
      await createPost({
        userId: currentUser.id,
        username: currentUser.username,
        userPhotoURL: currentUser.photoURL,
        subject: subject.trim(),
        photo: photoURL.trim(),
        content: content.trim(),
        factRating,
        topic: topic.trim()
      });
      
      navigate('/home');
    } catch (error) {
      console.error('Error creating post:', error);
      setError(`Failed to create post: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    }
  };
  
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Cancel</span>
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={isLoading || !subject.trim() || !content.trim() || !photoURL.trim() || isUploading || photoURL.startsWith('blob:')}
          className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors disabled:bg-purple-400/50 disabled:text-white/50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Publishing...' : 'Publish Post'}
        </button>
      </div>
      
      <div className="backdrop-blur-md bg-[#272727] rounded-xl p-6">
        {error && (
          <div className="mb-6 bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6">
          <label htmlFor="subject" className="block text-sm font-medium text-gray-400 mb-2">
            Post Title
          </label>
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter a compelling headline"
            className="w-full px-4 py-3 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors"
            maxLength={100}
          />
        </div>

        <div className="mb-6">
          <TopicSelect value={topic} onChange={setTopic} />
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-400 mb-2">
            Cover Image
          </label>
          
          <div className="rounded-lg overflow-hidden bg-[#272727] border-2 border-dashed border-[#404040]">
            {isCropping ? (
              <div className="space-y-4 p-4">
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
                  <Cropper
                    image={photoURL}
                    crop={crop}
                    zoom={zoom}
                    aspect={16/9}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div className="flex items-center space-x-2 flex-1">
                    <span className="text-xs text-gray-400 whitespace-nowrap">Zoom:</span>
                    <input
                      type="range"
                      value={zoom}
                      min={1}
                      max={3}
                      step={0.1}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="flex-1 min-w-0"
                    />
                  </div>
                  <button
                    onClick={handleCropSave}
                    disabled={isUploading}
                    className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors flex items-center justify-center space-x-2 disabled:bg-purple-400/50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isUploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        <Crop className="h-5 w-5" />
                        <span>Save Image</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : photoURL && !photoURL.startsWith('blob:') ? (
              <div className="relative aspect-video group">
                <img
                  src={photoURL}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                  ) : (
                    <Camera className="h-8 w-8 text-white" />
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="w-full aspect-video flex flex-col items-center justify-center text-gray-400 hover:text-white transition-colors"
              >
                {isUploading ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-current border-t-transparent" />
                ) : (
                  <>
                    <Camera className="h-12 w-12 mb-2" />
                    <span className="text-sm">Click to upload cover image</span>
                    <span className="text-xs text-gray-500 mt-1">JPG, PNG, GIF or WebP (max. 5MB)</span>
                  </>
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label htmlFor="content" className="block text-sm font-medium text-gray-400 mb-2">
            Post Content
          </label>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your post here..."
            className="w-full px-4 py-3 bg-[#272727] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#404040] hover:border-[#505050] transition-colors min-h-[200px] resize-none"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-400 mb-3">
            Fact Rating
          </label>
          <div className="space-y-2">
            {factRatingOptions.map((option) => (
              <div 
                key={option.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  factRating === option.value 
                    ? 'border-[#505050] bg-[#404040]' 
                    : 'border-[#404040] hover:border-[#505050]'
                }`}
                onClick={() => setFactRating(option.value as 'high' | 'medium' | 'low')}
              >
                <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                  factRating === option.value ? 'bg-[#505050]' : 'bg-[#404040]'
                }`}>
                  {factRating === option.value && <Check className="h-3 w-3 text-white" />}
                </div>
                <div className="flex items-center flex-1">
                  <div className={`w-3 h-3 rounded-full ${option.color} mr-3`}></div>
                  <span className="text-white">{option.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePost;